import { TextsContext } from "context/textsContext";
import { 
    createNestedErrorStructure,
    getAge,
    getAgeInDays,
    getEmptyRequiredFields,
    hasActiveErrors,
    setErrorMessage,
    validatePersonName 
} from "helpers/toolsHelper";
import { useContext, useMemo } from "react";
import useFormValidation from "./useFormValidation";
import { useCotizador } from "context/apiContext";
import { 
    DEPENDENT_FIELDS,
    DEPENDENT_RELATIONSHIPS,
    ERROR_TYPES,
    VALIDATION_CONSTRAINTS
} from "constants/validationConstants";

const useFormValidateDependent = () => {
    const { texts } = useContext(TextsContext);
    const { errors, setErrors, contextState } = useCotizador();
    const { validateBirthdayAge } = useFormValidation();

    const requiredFields = useMemo(() => DEPENDENT_FIELDS, []);

    const validateDependentFormFields = () => {
        const validationErrors = createNestedErrorStructure({}, 'dependants', '');
        const currentDependentErrors = errors?.dependants ?? {};
        const dependentData = contextState?.dependantData;

        const requiredFieldMessage = texts?.general_field_validation_required ?? 'This field is required';
        const dependentAgeMessage = texts?.general_field_validation_dependent_age ?? 
          'The age of the dependent cannot be older than 23 years.';
        const dependentAgeDaysMessage = texts?.general_field_validation_dependent_age_day ?? 
          'The dependent should be at least 30 days old.';
        const spouseValidationMessage = texts?.general_field_validation_spouse ?? 
          'You can only select one spouse';
        const nameValidationMessage = texts?.general_field_validation_name ?? 'Name should not contain numbers';
        const lastNameValidationMessage = texts?.general_field_validation_last_name ?? 'Last name should not contain numbers';
        

        requiredFields.forEach((fieldKey) => {
            validationErrors.dependants[fieldKey] = currentDependentErrors[fieldKey] ?? {}
        })

        if (!dependentData) {
            const emptyFields = getEmptyRequiredFields({}, requiredFields);
            emptyFields.forEach(fieldKey => {
                setErrorMessage(
                    validationErrors.dependants[fieldKey], 
                    ERROR_TYPES.EMPTY, 
                    requiredFieldMessage
                );
            });
        } else {
            const emptyFields = getEmptyRequiredFields(dependentData, requiredFields);

            requiredFields.forEach(fieldKey => {
                const isFieldEmpty = emptyFields.includes(fieldKey);
                setErrorMessage(
                    validationErrors.dependants[fieldKey],
                    ERROR_TYPES.EMPTY,
                    isFieldEmpty ? requiredFieldMessage : null
                );
            });

            const relationshipType = dependentData.componentId;
            const isChild = relationshipType === DEPENDENT_RELATIONSHIPS.CHILD;
            const isOther = relationshipType === DEPENDENT_RELATIONSHIPS.OTHER;
            const isSpouse = relationshipType === DEPENDENT_RELATIONSHIPS.SPOUSE;

            if(dependentData.birthday) {
                const dependentAge = getAge(dependentData.birthday);
                const dependentAgeInDays = getAgeInDays(dependentData.birthday);

                const isChildTooOld = dependentAge > VALIDATION_CONSTRAINTS.MAX_CHILD_AGE && isChild;

                setErrorMessage(
                    validationErrors.dependants.birthday,
                    ERROR_TYPES.INVALID_INPUT_SON,
                    isChildTooOld ? dependentAgeMessage : null
                );

                const isOtherTooOld = dependentAge > VALIDATION_CONSTRAINTS.MAX_CHILD_AGE && isOther;

                setErrorMessage(
                    validationErrors.dependants.birthday,
                    ERROR_TYPES.INVALID_INPUT_OTHER,
                    isOtherTooOld ? dependentAgeMessage : null
                );

                const isTooYoungInDays = dependentAgeInDays < VALIDATION_CONSTRAINTS.MIN_DEPENDENT_AGE_DAYS && 
                    (isChild || isOther);

                if (isTooYoungInDays) {
                    setErrorMessage(
                        validationErrors.dependants.birthday,
                        ERROR_TYPES.INVALID_INPUT_OTHER,
                        dependentAgeDaysMessage
                    );
                }

                if(isSpouse) {
                    const spouseBirthdayError = validateBirthdayAge(
                        dependentData.birthday, 
                        contextState.coverage, 
                        relationshipType
                    );

                    setErrorMessage(
                        validationErrors.dependants.birthday,
                        ERROR_TYPES.INVALID_INPUT_SPOUSE,
                        spouseBirthdayError
                    );
                } else {
                    setErrorMessage(
                        validationErrors.dependants.birthday,
                        ERROR_TYPES.INVALID_INPUT_SPOUSE,
                        null
                    );
                }
            }

            if (dependentData.name) {
                setErrorMessage(
                    validationErrors.dependants.name,
                    ERROR_TYPES.INVALID,
                    validatePersonName(dependentData.name) ? null : nameValidationMessage
                );
            }

            if (dependentData.surname) {
                setErrorMessage(
                    validationErrors.dependants.surname,
                    ERROR_TYPES.INVALID,
                    validatePersonName(dependentData.surname) ? null : lastNameValidationMessage
                );
            }

            if (isSpouse) {
                const existingSpouse = contextState.dependants.find(dep => 
                    dep.componentId === DEPENDENT_RELATIONSHIPS.SPOUSE
                );

                const isAddingDuplicateSpouse = existingSpouse && 
                    dependentData?.id !== existingSpouse.id;

                setErrorMessage(
                    validationErrors.dependants.componentId,
                    ERROR_TYPES.INVALID,
                    isAddingDuplicateSpouse ? spouseValidationMessage : null
                );
            } else {
                setErrorMessage(
                    validationErrors.dependants.componentId,
                    ERROR_TYPES.INVALID,
                    null
                );
            }
        }
        
        setErrors({
            ...errors,
            ...validationErrors
        });

        return hasActiveErrors(validationErrors.dependants);
    
    }

    return {
        validateDependantValues: validateDependentFormFields
    }
}

export default useFormValidateDependent;